import { useCallback, useEffect, useState } from 'react';
import {
  adminApi,
  AdminGesellschaft,
  AdminGesellschaftCreate,
  AdminGesellschaftUpdate,
} from '../../../../services/api/admin';

interface UseAdminGesellschaftenReturn {
  gesellschaften: AdminGesellschaft[];
  rechtsformOptions: string[];
  loading: boolean;
  error: string | null;
  create: (data: AdminGesellschaftCreate) => Promise<void>;
  update: (id: string, data: AdminGesellschaftUpdate) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

export const useAdminGesellschaften = (orgId: string): UseAdminGesellschaftenReturn => {
  const [gesellschaften, setGesellschaften] = useState<AdminGesellschaft[]>([]);
  const [rechtsformOptions, setRechtsformOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, options] = await Promise.all([
        adminApi.getGesellschaften(orgId),
        adminApi.getRechtsformOptions(),
      ]);
      setGesellschaften(data);
      setRechtsformOptions(options);
    } catch {
      setError('Gesellschaftsdaten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(
    async (data: AdminGesellschaftCreate) => {
      const created = await adminApi.createGesellschaft(orgId, data);
      setGesellschaften((prev) => [...prev, created]);
    },
    [orgId],
  );

  const update = useCallback(
    async (id: string, data: AdminGesellschaftUpdate) => {
      const updated = await adminApi.updateGesellschaft(orgId, id, data);
      setGesellschaften((prev) => prev.map((g) => (g.id === id ? updated : g)));
    },
    [orgId],
  );

  const remove = useCallback(
    async (id: string) => {
      await adminApi.deleteGesellschaft(orgId, id);
      setGesellschaften((prev) => prev.filter((g) => g.id !== id));
    },
    [orgId],
  );

  return { gesellschaften, rechtsformOptions, loading, error, create, update, remove };
};
